import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiRoutes } from '../../../services/ApiService';
import { showToast } from '../../../services/ToastService';
import { Email, EmailSubmissionData } from '../../../types/Email';
import Heading from '../../../components/Heading';
import styles from './ViewEmail.module.css';
import cx from 'classnames';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import IconButton from '../../../components/IconButton';
import { openModal, closeModal } from '../../../services/ModalService';
import { DeleteEmailModal } from '../DeleteEmailModal';
import { deleteEmail } from '../../../services/EmailService';

const ViewEmail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [email, setEmail] = useState<Email | null>(null);
    const [submissionData, setSubmissionData] = useState<EmailSubmissionData | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const apiUrl = useMemo(() => api.getBaseUrl(), []);

    useEffect(() => {
        const loadEmail = async () => {
            if (!id) {
                showToast('Ungültige E-Mail-ID', 'error');
                navigate('/emails');
                return;
            }   

            try {
                setIsLoading(true);
                const response = await api.get(ApiRoutes.EMAILS, parseInt(id));
                const emailData = response.data?.data?.email;
                
                if (!emailData || !emailData.data) {
                    showToast('E-Mail nicht gefunden', 'error');
                    navigate('/emails');
                    return;
                }

                setEmail({
                    id: emailData.id,
                    email: emailData.data.userData?.email || '',
                    company: emailData.data.userData?.company || '',
                });
                setSubmissionData(emailData.data);
            } catch (error) {
                console.error('Failed to load email:', error);
                showToast('Fehler beim Laden der E-Mail', 'error');
                navigate('/emails');
            } finally {
                setIsLoading(false);
            }
        };

        loadEmail();
    }, [id, navigate]);

    const formatPrice = (price: number): string => {
        if (price === 0) {
            return 'kostenlos';
        }
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(price);
    };

    const handleLogoDownload = async (logoUrl: string, downloadFilename: string) => {
        try {
            const response = await fetch(logoUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = downloadFilename || 'logo';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download logo:', error);
        }
    };

    const NON_IMAGE_EXTENSIONS = ['pdf', 'eps', 'ai'];

    const placeholderImageUrl = useMemo(() => {
        const apiUrl = api.getBaseUrl();
        return `${apiUrl}storage/placeholder/tsoutfit.png`;
    }, []);

    const handleDeleteEmail = useCallback(async (emailToDelete: Email) => {
        try {
            const success = await deleteEmail(emailToDelete.id);
            if (success) {
                showToast('E-Mail erfolgreich gelöscht', 'success');
                navigate('/emails');
            } else {
                showToast('Fehler beim Löschen der E-Mail', 'error');
            }
        } catch (error) {
            console.error('Failed to delete email:', error);
            showToast('Fehler beim Löschen der E-Mail', 'error');
        }
    }, [navigate]);

    const openDeleteEmailModal = useCallback((emailToDelete: Email) => {
        const modalIdRef = { current: '' };
        modalIdRef.current = openModal({
            content: (
                <DeleteEmailModal 
                    emailId={emailToDelete.id} 
                    emailAddress={emailToDelete.email} 
                    onConfirm={() => {
                        handleDeleteEmail(emailToDelete);
                        closeModal(modalIdRef.current);
                    }} 
                    onCancel={() => closeModal(modalIdRef.current)} 
                />
            ),
            size: 'sm',
            onClose: () => closeModal(modalIdRef.current)
        });
    }, [handleDeleteEmail]);

    if (isLoading) {
        return (
            <div className={cx(styles['loading-container'])}>
                <div>Lade E-Mail...</div>
            </div>
        );
    }

    if (!email || !submissionData) {
        return null;
    }

    return (
        <div className={cx(styles['preview-step-container'])}>
            <div className={cx(styles['email-header-section'])}>
                <div className={'zr-d-flex zr-flex-column zr-gap-2 zr-items-start'}>
                    <Heading level={3} classes={cx(styles['email-title'])}>{email.company || email.email}</Heading>
                    <a className={cx(styles['back-button'])}
                       onClick={() => navigate('/emails')}
                    >Zurück zur Übersicht</a>
                </div>
              <IconButton icon={faTrashAlt} color="#282525" title="Löschen" onClick={() => openDeleteEmailModal(email)}  />
            
            </div>
            
            <div className={cx(styles['preview-columns'])}>
                {/* User Data Column */}
                <div className={cx(styles['preview-column'])}>
                    <div className={cx(styles['preview-card'])}>
                        <h3 className={cx(styles['card-title'])}>Benutzerdaten</h3>
                        <div className={cx(styles['card-content'])}>
                            {/* Types Section */}
                            {submissionData.types && submissionData.types.length > 0 && (
                                <div className={cx(styles['types-section'])}>
                                    <span className={cx(styles['data-label'])}>Branche:</span>
                                    <div className={cx(styles['types-list'])}>
                                        {submissionData.types.map((type, index) => (
                                            <span key={index} className={cx(styles['type-badge'])}>
                                                {type.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* User Data Grid */}
                            <div className={cx(styles['user-data-grid'])}>
                                {submissionData.userData?.company && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Firma:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.company}</span>
                                    </div>
                                )}
                                {submissionData.userData?.customTypeName && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Andere Branche:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.customTypeName}</span>
                                    </div>
                                )}
                                {submissionData.userData?.firstName && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Vorname:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.firstName}</span>
                                    </div>
                                )}
                                {submissionData.userData?.lastName && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Nachname:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.lastName}</span>
                                    </div>
                                )}
                                {submissionData.userData?.street && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Straße:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.street}</span>
                                    </div>
                                )}
                                {submissionData.userData?.houseNumber && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Hausnummer:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.houseNumber}</span>
                                    </div>
                                )}
                                {submissionData.userData?.postalCode && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Postleitzahl:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.postalCode}</span>
                                    </div>
                                )}
                                {submissionData.userData?.city && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Ort:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.city}</span>
                                    </div>
                                )}
                                {submissionData.userData?.email && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>E-Mail:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.email}</span>
                                    </div>
                                )}
                                {submissionData.userData?.phoneNumber && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Telefonnummer:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.phoneNumber}</span>
                                    </div>
                                )}
                                {submissionData.userData?.memberCount && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Anzahl Mitarbeiter:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.memberCount}</span>
                                    </div>
                                )}
                                {submissionData.userData?.additionalInformation && (
                                    <div className={cx(styles['data-item'], styles['data-item-full'])}>
                                        <span className={cx(styles['data-label'])}>Zusätzliche Informationen:</span>
                                        <span className={cx(styles['data-value'])}>{submissionData.userData.additionalInformation}</span>
                                    </div>
                                )}
                            </div>

                            {/* CI Section Inline */}
                            <div className={cx(styles['ci-section-inline'])}>
                                {(submissionData.primaryColor || submissionData.secondaryColor) && (
                                    <div className={cx(styles['ci-row'])}>
                                        {submissionData.primaryColor && (
                                            <div className={cx(styles['ci-color-item'])}>
                                                <span className={cx(styles['data-label'])}>Primärfarbe:</span>
                                                <div
                                                    className={cx(styles['ci-color-box'])}
                                                    style={{ backgroundColor: submissionData.primaryColor }}
                                                />
                                            </div>
                                        )}
                                        {submissionData.secondaryColor && (
                                            <div className={cx(styles['ci-color-item'])}>
                                                <span className={cx(styles['data-label'])}>Sekundärfarbe:</span>
                                                <div
                                                    className={cx(styles['ci-color-box'])}
                                                    style={{ backgroundColor: submissionData.secondaryColor }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {(submissionData.font || submissionData.style) && (
                                    <div className={cx(styles['ci-row'])}>
                                        {submissionData.font && (
                                            <div className={cx(styles['data-item'])}>
                                                <span className={cx(styles['data-label'])}>Schriftart:</span>
                                                <span className={cx(styles['data-value'])}>{submissionData.font}</span>
                                            </div>
                                        )}
                                        {submissionData.style && (
                                            <div className={cx(styles['data-item'])}>
                                                <span className={cx(styles['data-label'])}>Stil:</span>
                                                <span className={cx(styles['data-value'])}>{submissionData.style}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Logos */}
                                {submissionData.logoFiles && submissionData.logoFiles.length > 0 && (
                                    <div className={cx(styles['data-item'])}>
                                        <span className={cx(styles['data-label'])}>Logos:</span>
                                        <div className={cx(styles['logos-list-inline'])}>
                                            {submissionData.logoFiles.map((logoFile, index) => {
                                                const logoUrl = `${apiUrl}${logoFile.url}`;
                                                const ext = logoFile.filename?.split('.').pop()?.toLowerCase() ?? '';
                                                const isPdfEpsAi = NON_IMAGE_EXTENSIONS.includes(ext);
                                                const downloadName = logoFile.filename || logoFile.label || 'logo';
                                                const onDownload = () => handleLogoDownload(logoUrl, downloadName);
                                                return (
                                                    <div key={index} className={cx(styles['logo-item-inline'])}>
                                                        {isPdfEpsAi ? (
                                                            <button
                                                                type="button"
                                                                className={cx(styles['logo-file-download'])}
                                                                onClick={onDownload}
                                                                title={`${ext.toUpperCase()} herunterladen`}
                                                            >
                                                                <span className={cx(styles['logo-file-badge'])}>
                                                                    {ext.toUpperCase()}
                                                                </span>
                                                            </button>
                                                        ) : (
                                                            <img
                                                                src={logoUrl}
                                                                alt={logoFile.label || 'Logo'}
                                                                className={cx(styles['logo-preview-inline'])}
                                                                onClick={onDownload}
                                                                style={{ cursor: 'pointer' }}
                                                                onError={(e) => {
                                                                    (e.target as HTMLImageElement).style.display = 'none';
                                                                }}
                                                            />
                                                        )}
                                                        <span className={cx(styles['logo-label-inline'])}>
                                                            {logoFile.label || logoFile.filename}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Articles Column */}
                <div className={cx(styles['preview-column'])}>
                    {submissionData.articles && submissionData.articles.length > 0 && (
                        <div className={cx(styles['preview-card'])}>
                            <h3 className={cx(styles['card-title'])}>Ausgewählte Artikel ({submissionData.articles.length})</h3>
                            <div className={cx(styles['card-content'])}>
                                <div className={cx(styles['articles-grid'])}>
                                    {submissionData.articles.map((article) => (
                                        <div key={article.id} className={cx(styles['article-mini-card'])}>
                                            <div className={cx(styles['article-mini-image'])}>
                                                <img 
                                                    src={article.imageUrl || placeholderImageUrl} 
                                                    alt={article.name}
                                                    className={cx(styles['article-mini-img'])}
                                                />
                                            </div>
                                            <div className={cx(styles['article-mini-info'])}>
                                                <span className={cx(styles['article-mini-name'])}>{article.name}</span>
                                                {article.articleNumber && (
                                                    <span className={cx(styles['article-mini-number'])}>Art.-Nr.: {article.articleNumber}</span>
                                                )}
                                                <span className={cx(styles['article-mini-price'])}>{formatPrice(article.price)}</span>
                                                {article.url && (
                                                    <a 
                                                        href={article.url} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className={cx(styles['article-mini-link'])}
                                                    >
                                                        Zum Artikel →
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewEmail;
